import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLayoutEffect, useRef, useState } from 'react';
import { api } from '../../lib/api-client';
import type { HomeContextSnapshot, Personalization } from '../../lib/api-types';
import { keys } from '../../lib/queries';
import { MarkdownContent } from './MarkdownContent';
import styles from './GoalEditor.module.css';

/** The original Home goal, frozen with the first message and never editable. */
export function GoalSnapshot({ snapshot }: { snapshot: HomeContextSnapshot }) {
  return <section className={`${styles.goal} ${styles.snapshot}`} aria-label="Goal when this chat started">
    {snapshot.goals.trim() || snapshot.currentMission ? <>
      {snapshot.currentMission && <h2 className={styles.heading}>{snapshot.currentMission}</h2>}
      {snapshot.goals.trim() && <div className={styles.form}>
        <span className={styles.label}>Your goal</span>
        <div className={styles.snapshotText}><MarkdownContent source={snapshot.goals} /></div>
      </div>}
      {snapshot.nextStepHint && <p className={styles.nextStep}>{snapshot.nextStepHint}</p>}
    </> : <>
      <h2 className={styles.heading}>Tell me about your business.</h2>
      <p className={styles.nextStep}>Echo infers your goal as you chat, then gets to work setting things up for you.</p>
    </>}
  </section>;
}

/** Home and Settings share the saved goal, its presentation, and its editor. */
export function GoalEditor({ personal }: { personal: Personalization }) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const input = useRef<HTMLTextAreaElement>(null);
  const preview = useRef<HTMLDivElement>(null);
  const returnFocus = useRef(false);
  const value = draft ?? personal.goals;
  const dirty = value !== personal.goals;
  const finishEditing = () => {
    setDraft(null);
    setEditing(false);
    returnFocus.current = true;
  };
  const mutation = useMutation({
    mutationFn: (goals: string) => api.updatePersonalization({ goals }),
    onSuccess: result => {
      client.setQueryData(keys.personalization, result);
      finishEditing();
      setSaved(true);
    },
  });

  useLayoutEffect(() => {
    if (editing) input.current?.focus();
    else if (returnFocus.current) {
      returnFocus.current = false;
      preview.current?.focus();
    }
  }, [editing]);
  useLayoutEffect(() => {
    if (!input.current) return;
    input.current.style.height = 'auto';
    input.current.style.height = `${input.current.scrollHeight}px`;
  }, [editing, value]);

  const edit = () => {
    setEditing(true);
    setSaved(false);
    mutation.reset();
  };
  const cancel = () => {
    finishEditing();
    mutation.reset();
  };

  return <div className={styles.goal}>
    {personal.currentMission && <h2 className={styles.heading}>{personal.currentMission}</h2>}
    <form className={styles.form} aria-label="Your goal" onSubmit={event => {
      event.preventDefault();
      if (dirty && !mutation.isPending) mutation.mutate(value);
    }}>
      <span className={styles.label}>Your goal</span>
      {editing ? <textarea ref={input} className={styles.input} aria-label="Your goal"
        value={value} rows={1} maxLength={100_000} disabled={mutation.isPending}
        placeholder="What would you like to accomplish?"
        onChange={event => { setDraft(event.target.value); mutation.reset(); }}
        onBlur={() => { if (!dirty) setEditing(false); }}
        onKeyDown={event => {
          if (event.key === 'Escape' && !mutation.isPending) { event.preventDefault(); cancel(); }
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault(); event.currentTarget.form?.requestSubmit();
          }
        }} />
        : <div ref={preview} className={styles.preview} role="button" tabIndex={0} aria-label="Edit goal"
          onClick={event => { if (!(event.target as HTMLElement).closest('a')) edit(); }}
          onKeyDown={event => {
            if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
              event.preventDefault(); edit();
            }
          }}>
          {personal.goals.trim() ? <MarkdownContent source={personal.goals} />
            : <span className={styles.empty}>What would you like to accomplish?</span>}
        </div>}
      {dirty && <div className={styles.actions}>
        <button type="submit" className={styles.save} disabled={mutation.isPending}>{mutation.isPending ? 'Saving…' : 'Save'}</button>
        <button type="button" className={styles.cancel} disabled={mutation.isPending} onClick={cancel}>Cancel</button>
      </div>}
      {mutation.isError && <p className={styles.error} role="alert">Couldn’t save your goal. {mutation.error.message}</p>}
      {saved && <span className={styles.status} role="status">Goal saved.</span>}
    </form>
    {personal.nextStepHint && <p className={styles.nextStep}>{personal.nextStepHint}</p>}
  </div>;
}
